import { useState } from 'react'
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/use-auth'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import {
  apiV2,
  type AdminNonprofitApplication,
  type AdminNonprofitBlock,
  type AdminNonprofitDomain,
  type AdminNonprofitGrant,
  type AdminNonprofitImport,
  type NonprofitApplication,
  type NonprofitGrant,
} from '@/lib/api-v2-client'
import { STATUS_BADGES, isHttpsUrl, normalizeDomain, sourceLabel } from '@/lib/nonprofit'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DataTable } from '@/components/data-table'
import { EmptyState, ErrorState, LoadingState } from '@/components/state-view'
import { StatusBadge, type StatusKind } from '@/components/status-badge'

export const Route = createFileRoute('/_authenticated/admin_/nonprofits')({
  component: AdminNonprofitsPage,
})

const APPLICATION_STATUSES: NonprofitApplication['status'][] = ['pending', 'approved', 'rejected']
const GRANT_STATUSES: NonprofitGrant['status'][] = ['active', 'ended', 'revoked']

/** The approve and reject dialogs. Closed, the dialog keeps the reject texts (no flash while it fades). */
const DECIDE = {
  approve: {
    title: (domain: string) => `Approve ${domain}`,
    description: 'The organization gets Pro for 12 months and the domain joins the list as an approved application.',
    label: 'Note (optional)',
    confirmLabel: 'Approve',
    required: false,
    destructive: false,
  },
  reject: {
    title: (domain: string) => `Reject ${domain}`,
    description: 'The owners and admins get this note by email.',
    label: 'Reason (sent to the organization)',
    confirmLabel: 'Reject',
    required: true,
    destructive: true,
  },
} as const

const SELECT_CLASS = 'flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs'

function day(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function errorText(err: unknown): string {
  return err instanceof Error && err.message ? err.message : 'Something went wrong'
}

/** Platform admin: the nonprofit program (OpenSpec 136). Queue, grants, lists, imports. */
export function AdminNonprofitsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isAdmin = !!user?.is_admin
  const [appStatus, setAppStatus] = useState<NonprofitApplication['status']>('pending')
  const [grantStatus, setGrantStatus] = useState<NonprofitGrant['status']>('active')
  const [domainQuery, setDomainQuery] = useState('')
  const debouncedDomainQuery = useDebouncedValue(domainQuery, 300)

  const applications = useQuery({
    queryKey: ['admin-nonprofit-applications', appStatus],
    queryFn: () => apiV2.adminListNonprofitApplications(appStatus),
    enabled: isAdmin,
  })
  const grants = useQuery({
    queryKey: ['admin-nonprofit-grants', grantStatus],
    queryFn: () => apiV2.adminListNonprofitGrants(grantStatus),
    enabled: isAdmin,
  })
  const domains = useQuery({
    queryKey: ['admin-nonprofit-domains', debouncedDomainQuery],
    queryFn: () => apiV2.adminListNonprofitDomains(debouncedDomainQuery),
    enabled: isAdmin,
  })
  const blocks = useQuery({
    queryKey: ['admin-nonprofit-blocks'],
    queryFn: () => apiV2.adminListNonprofitBlocks(),
    enabled: isAdmin,
  })
  const imports = useQuery({
    queryKey: ['admin-nonprofit-imports'],
    queryFn: () => apiV2.adminListNonprofitImports(),
    enabled: isAdmin,
  })

  // Guard after hooks so their call order remains stable between auth states.
  if (!isAdmin) {
    navigate({ to: '/dashboard' })
    return null
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 pb-20">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Nonprofit program</h1>
          <p className="text-sm text-muted-foreground">Applications, grants, the domain lists and the register imports. All writes are audited.</p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/admin">Admin · Users</Link>
        </Button>
      </div>

      <ApplicationsSection
        status={appStatus}
        onStatus={setAppStatus}
        rows={applications.data?.data}
        isLoading={applications.isLoading}
        error={applications.error}
        onRetry={() => void applications.refetch()}
      />
      <GrantsSection
        status={grantStatus}
        onStatus={setGrantStatus}
        rows={grants.data?.data}
        isLoading={grants.isLoading}
        error={grants.error}
        onRetry={() => void grants.refetch()}
      />
      <DomainsSection
        query={domainQuery}
        onQuery={setDomainQuery}
        rows={domains.data?.data}
        isLoading={domains.isLoading}
        error={domains.error}
        onRetry={() => void domains.refetch()}
      />
      <BlocksSection rows={blocks.data?.data} isLoading={blocks.isLoading} error={blocks.error} onRetry={() => void blocks.refetch()} />
      <ImportsSection rows={imports.data?.data} isLoading={imports.isLoading} error={imports.error} onRetry={() => void imports.refetch()} />
    </div>
  )
}

type ListProps<T> = { rows: T[] | undefined; isLoading: boolean; error: unknown; onRetry: () => void }

function ListState<T>({ rows, isLoading, error, onRetry, empty, children }: ListProps<T> & { empty: string; children: (rows: T[]) => React.ReactNode }) {
  if (isLoading) return <LoadingState rows={2} />
  if (error) return <ErrorState message={errorText(error)} onRetry={onRetry} />
  if (!rows || rows.length === 0) return <EmptyState title={empty} className="py-8" />
  return <>{children(rows)}</>
}

function StatusFilter<T extends keyof typeof STATUS_BADGES>({ id, value, options, onChange }: { id: string; value: T; options: T[]; onChange: (v: T) => void }) {
  return (
    <select id={id} aria-label="Filter by status" className={SELECT_CLASS} value={value} onChange={(e) => onChange(e.target.value as T)}>
      {options.map((o) => (
        <option key={o} value={o}>
          {STATUS_BADGES[o].label}
        </option>
      ))}
    </select>
  )
}

/** A confirm dialog with a note field. `required`: the server needs the note (reject, revoke). */
function NoteDialog({
  open,
  title,
  description,
  label,
  required,
  confirmLabel,
  destructive,
  pending,
  onClose,
  onConfirm,
}: {
  open: boolean
  title: string
  description: string
  label: string
  required: boolean
  confirmLabel: string
  destructive?: boolean
  pending: boolean
  onClose: () => void
  onConfirm: (note: string) => void
}) {
  const [note, setNote] = useState('')
  const ok = !required || note.trim().length > 0
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setNote('')
          onClose()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor="np-note">{label}</Label>
          <textarea
            id="np-note"
            rows={3}
            maxLength={1000}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            disabled={!ok || pending}
            onClick={() => {
              onConfirm(note.trim())
              setNote('')
            }}
          >
            {pending ? 'Saving…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function useInvalidate() {
  const qc = useQueryClient()
  return (...keys: string[]) => Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: [k] })))
}

// ── Applications ──

function ApplicationsSection({
  status,
  onStatus,
  ...list
}: ListProps<AdminNonprofitApplication> & { status: NonprofitApplication['status']; onStatus: (s: NonprofitApplication['status']) => void }) {
  const invalidate = useInvalidate()
  const [action, setAction] = useState<{ kind: 'approve' | 'reject'; row: AdminNonprofitApplication } | null>(null)

  const decide = useMutation({
    mutationFn: async ({ kind, id, note }: { kind: 'approve' | 'reject'; id: string; note: string }) => {
      if (kind === 'approve') await apiV2.adminApproveNonprofitApplication(id, note || undefined)
      else await apiV2.adminRejectNonprofitApplication(id, note)
    },
    onSuccess: async (_r, v) => {
      toast.success(v.kind === 'approve' ? 'Approved. The organization is on Pro.' : 'Rejected. The owners get the note by email.')
      setAction(null)
      // Approve adds a grant and a domain row; reject changes only the application.
      if (v.kind === 'approve') await invalidate('admin-nonprofit-applications', 'admin-nonprofit-grants', 'admin-nonprofit-domains')
      else await invalidate('admin-nonprofit-applications')
    },
    onError: (err) => toast.error(errorText(err)),
  })
  const dialog = DECIDE[action?.kind ?? 'reject']

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Applications</CardTitle>
            <CardDescription>Check the registry number against the public register before you approve.</CardDescription>
          </div>
          <StatusFilter id="np-app-status" value={status} options={APPLICATION_STATUSES} onChange={onStatus} />
        </div>
      </CardHeader>
      <CardContent>
        <ListState {...list} empty={`No ${STATUS_BADGES[status].label.toLowerCase()} applications.`}>
          {(rows) => (
            <DataTable
              label="Applications"
              rows={rows}
              getKey={(r) => r.id}
              columns={[
                {
                  header: 'Organization',
                  cell: (r) => (
                    <div className="min-w-40">
                      <div className="font-medium">{r.organization_name}</div>
                      <div className="break-all text-muted-foreground">{r.user_email ?? '—'}</div>
                    </div>
                  ),
                },
                { header: 'Domain', cell: (r) => <span className="font-mono">{r.domain}</span> },
                {
                  header: 'Registered as',
                  cell: (r) => (
                    <div className="min-w-48">
                      <div>{r.legal_name}</div>
                      <div className="text-muted-foreground">
                        {r.country_code} · {r.registry_name} <span className="font-mono">{r.registry_id}</span>
                      </div>
                      {r.register_url && isHttpsUrl(r.register_url) && (
                        <a href={r.register_url} target="_blank" rel="noopener noreferrer" className="break-all text-primary hover:underline">
                          Register entry
                        </a>
                      )}
                    </div>
                  ),
                },
                { header: 'Sent', cell: (r) => <span className="num whitespace-nowrap">{day(r.inserted_at)}</span> },
                {
                  header: 'Status',
                  cell: (r) => (
                    <div className="space-y-1">
                      <StatusBadge kind={STATUS_BADGES[r.status].kind} label={STATUS_BADGES[r.status].label} />
                      {r.decision_note && <div className="text-muted-foreground">{r.decision_note}</div>}
                    </div>
                  ),
                },
                {
                  header: <span className="sr-only">Actions</span>,
                  align: 'right',
                  cell: (r) =>
                    r.status === 'pending' ? (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setAction({ kind: 'approve', row: r })}>
                          Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setAction({ kind: 'reject', row: r })}>
                          Reject
                        </Button>
                      </div>
                    ) : null,
                },
              ]}
            />
          )}
        </ListState>
      </CardContent>
      <NoteDialog
        open={!!action}
        title={dialog.title(action?.row.domain ?? '')}
        description={dialog.description}
        label={dialog.label}
        required={dialog.required}
        confirmLabel={dialog.confirmLabel}
        destructive={dialog.destructive}
        pending={decide.isPending}
        onClose={() => setAction(null)}
        onConfirm={(note) => action && decide.mutate({ kind: action.kind, id: action.row.id, note })}
      />
    </Card>
  )
}

// ── Grants ──

function GrantsSection({
  status,
  onStatus,
  ...list
}: ListProps<AdminNonprofitGrant> & { status: NonprofitGrant['status']; onStatus: (s: NonprofitGrant['status']) => void }) {
  const invalidate = useInvalidate()
  const [revoking, setRevoking] = useState<AdminNonprofitGrant | null>(null)

  const revoke = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => apiV2.adminRevokeNonprofitGrant(id, reason),
    onSuccess: async () => {
      toast.success('Grant revoked and the domain blocked. The plan was recomputed.')
      setRevoking(null)
      await invalidate('admin-nonprofit-grants', 'admin-nonprofit-blocks')
    },
    onError: (err) => toast.error(errorText(err)),
  })

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Grants</CardTitle>
            <CardDescription>Revoking ends Pro now and blocks the domain. Blocking a domain alone does not revoke its grant.</CardDescription>
          </div>
          <StatusFilter id="np-grant-status" value={status} options={GRANT_STATUSES} onChange={onStatus} />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <ListState {...list} empty={`No ${STATUS_BADGES[status].label.toLowerCase()} grants.`}>
          {(rows) => (
            <DataTable
              label="Grants"
              rows={rows}
              getKey={(r) => r.id}
              columns={[
                { header: 'Organization', cell: (r) => <span className="font-medium">{r.organization_name}</span> },
                { header: 'Domain', cell: (r) => <span className="font-mono">{r.domain}</span> },
                { header: 'Source', cell: (r) => sourceLabel(r.source) },
                { header: 'Starts', cell: (r) => <span className="num whitespace-nowrap">{day(r.starts_at)}</span> },
                { header: 'Ends', cell: (r) => <span className="num whitespace-nowrap">{day(r.ends_at)}</span> },
                { header: 'Status', cell: (r) => <StatusBadge kind={STATUS_BADGES[r.status].kind} label={STATUS_BADGES[r.status].label} /> },
                { header: 'Note', cell: (r) => <span className="text-muted-foreground">{r.note ?? '—'}</span> },
                {
                  header: <span className="sr-only">Actions</span>,
                  align: 'right',
                  cell: (r) =>
                    r.status === 'active' ? (
                      <Button size="sm" variant="outline" onClick={() => setRevoking(r)}>
                        Revoke
                      </Button>
                    ) : null,
                },
              ]}
            />
          )}
        </ListState>
        <DirectGrantForm onDone={() => invalidate('admin-nonprofit-grants')} />
      </CardContent>
      <NoteDialog
        open={!!revoking}
        title={`Revoke the grant for ${revoking?.domain ?? ''}`}
        description="The domain is blocked too, so the organization cannot claim again until you unblock it. The organization falls to the plan of its subscription, or Free, and the owners get an email."
        label="Reason (sent to the organization)"
        required
        confirmLabel="Revoke"
        destructive
        pending={revoke.isPending}
        onClose={() => setRevoking(null)}
        onConfirm={(reason) => revoking && revoke.mutate({ id: revoking.id, reason })}
      />
    </Card>
  )
}

function DirectGrantForm({ onDone }: { onDone: () => Promise<unknown> }) {
  const [orgId, setOrgId] = useState('')
  const [domain, setDomain] = useState('')
  const [months, setMonths] = useState('12')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [orgQuery, setOrgQuery] = useState('')
  const debouncedOrgQuery = useDebouncedValue(orgQuery.trim(), 300)

  // Find the organization id through the existing user search (this form is only mounted for admins).
  const orgSearch = useQuery({
    queryKey: ['admin-users', debouncedOrgQuery, 1, 'signed_up_desc', 'nonprofit-grant'],
    queryFn: () => apiV2.adminSearchUsers(debouncedOrgQuery, 1, 10),
    enabled: debouncedOrgQuery.length >= 2,
  })
  const orgs = [...new Map((orgSearch.data?.data ?? []).map((u) => [u.organization_id, u])).values()].slice(0, 5)

  const create = useMutation({
    mutationFn: (body: { organization_id: string; domain: string; months: number; note: string }) => apiV2.adminCreateNonprofitGrant(body),
    onSuccess: async () => {
      toast.success('Grant created. The organization is on Pro.')
      setOrgId('')
      setDomain('')
      setMonths('12')
      setNote('')
      await onDone()
    },
    onError: (err) => setError(errorText(err)),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const d = normalizeDomain(domain)
    const m = Number(months)
    if (!/^[0-9a-f-]{36}$/i.test(orgId.trim())) return setError('Enter the organization id (a UUID).')
    if (!d) return setError('Enter a domain such as helpers.org.')
    if (!Number.isInteger(m) || m < 1 || m > 24) return setError('Months must be a whole number from 1 to 24.')
    if (!note.trim()) return setError('Write why this grant is outside the rules.')
    setError(null)
    create.mutate({ organization_id: orgId.trim(), domain: d, months: m, note: note.trim() })
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-3 border-t border-border pt-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Direct grant</h3>
        <p className="text-sm text-muted-foreground">For cases outside the rules. Find the organization by a member's name or email.</p>
      </div>
      <div className="space-y-2">
        <Input
          type="search"
          aria-label="Find an organization by member name or email"
          placeholder="Find an organization by member name or email…"
          value={orgQuery}
          onChange={(e) => setOrgQuery(e.target.value)}
        />
        {orgs.length > 0 && (
          <ul className="divide-y divide-border rounded-md border border-border text-sm" aria-label="Organizations found">
            {orgs.map((u) => (
              <li key={u.organization_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <span className="min-w-0">
                  <span className="font-medium">{u.organization_name}</span>{' '}
                  <span className="break-all text-muted-foreground">{u.email}</span>
                </span>
                <Button type="button" size="sm" variant="ghost" onClick={() => setOrgId(u.organization_id)}>
                  Use
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="np-grant-org">Organization id</Label>
          <Input id="np-grant-org" className="font-mono" value={orgId} onChange={(e) => setOrgId(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="np-grant-domain">Domain</Label>
          <Input id="np-grant-domain" className="font-mono" placeholder="helpers.org" value={domain} onChange={(e) => setDomain(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="np-grant-months">Months (1 to 24)</Label>
          <Input id="np-grant-months" type="number" min={1} max={24} className="num w-28" value={months} onChange={(e) => setMonths(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="np-grant-note">Note</Label>
          <Input id="np-grant-note" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>
      {error && (
        <p role="alert" className="text-sm font-medium text-destructive">
          {error}
        </p>
      )}
      <Button type="submit" variant="outline" disabled={create.isPending}>
        {create.isPending ? 'Granting…' : 'Grant Pro'}
      </Button>
    </form>
  )
}

// ── Domains ──

function DomainsSection({ query, onQuery, ...list }: ListProps<AdminNonprofitDomain> & { query: string; onQuery: (q: string) => void }) {
  const invalidate = useInvalidate()
  const [domain, setDomain] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const add = useMutation({
    mutationFn: (v: { domain: string; note: string }) => apiV2.adminAddNonprofitDomain(v.domain, v.note || undefined),
    onSuccess: async () => {
      toast.success('Domain added to the curated list.')
      setDomain('')
      setNote('')
      await invalidate('admin-nonprofit-domains')
    },
    onError: (err) => setError(errorText(err)),
  })
  const remove = useMutation({
    mutationFn: (v: { domain: string; source: 'curated' | 'manual' }) => apiV2.adminRemoveNonprofitDomain(v.domain, v.source),
    onSuccess: async () => {
      toast.success('Domain removed. Its grant is not renewed.')
      await invalidate('admin-nonprofit-domains')
    },
    onError: (err) => toast.error(errorText(err)),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const d = normalizeDomain(domain)
    if (!d) return setError('Enter a domain such as helpers.org.')
    setError(null)
    add.mutate({ domain: d, note: note.trim() })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Domains</CardTitle>
        <CardDescription>
          Domains that qualify at once. Register rows come from the weekly import; only curated and approved rows can be removed here. Shows at most 100.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input type="search" aria-label="Search domains" placeholder="Search by domain or name…" value={query} onChange={(e) => onQuery(e.target.value)} />
        <ListState {...list} empty={query ? 'No domain matches.' : 'No domains yet.'}>
          {(rows) => (
            <DataTable
              label="Domains"
              rows={rows}
              getKey={(r) => `${r.domain}:${r.source}`}
              columns={[
                { header: 'Domain', cell: (r) => <span className="font-mono">{r.domain}</span> },
                { header: 'Source', cell: (r) => sourceLabel(r.source) },
                { header: 'Name', cell: (r) => r.org_name ?? '—' },
                { header: 'Registry number', cell: (r) => <span className="font-mono">{r.registry_id ?? '—'}</span> },
                { header: 'Note', cell: (r) => <span className="text-muted-foreground">{r.note ?? '—'}</span> },
                { header: 'Added', cell: (r) => <span className="num whitespace-nowrap">{day(r.inserted_at)}</span> },
                {
                  header: <span className="sr-only">Actions</span>,
                  align: 'right',
                  cell: (r) =>
                    r.source === 'curated' || r.source === 'manual' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate({ domain: r.domain, source: r.source as 'curated' | 'manual' })}
                      >
                        Remove
                      </Button>
                    ) : null,
                },
              ]}
            />
          )}
        </ListState>
        <form onSubmit={submit} noValidate className="space-y-2 border-t border-border pt-4">
          <h3 className="text-sm font-semibold text-foreground">Add a curated domain</h3>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input aria-label="Domain to add" className="font-mono sm:w-64" placeholder="helpers.org" value={domain} onChange={(e) => setDomain(e.target.value)} />
            <Input aria-label="Note" placeholder="Note (optional)" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
            <Button type="submit" variant="outline" disabled={add.isPending}>
              Add
            </Button>
          </div>
          {error && (
            <p role="alert" className="text-sm font-medium text-destructive">
              {error}
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  )
}

// ── Blocks ──

function BlocksSection(list: ListProps<AdminNonprofitBlock>) {
  const invalidate = useInvalidate()
  const [domain, setDomain] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const add = useMutation({
    mutationFn: (v: { domain: string; reason: string }) => apiV2.adminAddNonprofitBlock(v.domain, v.reason),
    onSuccess: async () => {
      toast.success('Domain blocked. Revoke its grant separately if it has one.')
      setDomain('')
      setReason('')
      await invalidate('admin-nonprofit-blocks')
    },
    onError: (err) => setError(errorText(err)),
  })
  const remove = useMutation({
    mutationFn: (d: string) => apiV2.adminRemoveNonprofitBlock(d),
    onSuccess: async () => {
      toast.success('Block removed.')
      await invalidate('admin-nonprofit-blocks')
    },
    onError: (err) => toast.error(errorText(err)),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const d = normalizeDomain(domain)
    if (!d) return setError('Enter a domain such as helpers.org.')
    if (!reason.trim()) return setError('Write the reason for the block.')
    setError(null)
    add.mutate({ domain: d, reason: reason.trim() })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Blocked domains</CardTitle>
        <CardDescription>A blocked domain never qualifies, whatever list it is on.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ListState {...list} empty="No blocked domains.">
          {(rows) => (
            <DataTable
              label="Blocked domains"
              rows={rows}
              getKey={(r) => r.domain}
              columns={[
                { header: 'Domain', cell: (r) => <span className="font-mono">{r.domain}</span> },
                { header: 'Reason', cell: (r) => r.reason },
                { header: 'Blocked', cell: (r) => <span className="num whitespace-nowrap">{day(r.inserted_at)}</span> },
                {
                  header: <span className="sr-only">Actions</span>,
                  align: 'right',
                  cell: (r) => (
                    <Button size="sm" variant="outline" disabled={remove.isPending} onClick={() => remove.mutate(r.domain)}>
                      Unblock
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </ListState>
        <form onSubmit={submit} noValidate className="space-y-2 border-t border-border pt-4">
          <h3 className="text-sm font-semibold text-foreground">Block a domain</h3>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input aria-label="Domain to block" className="font-mono sm:w-64" placeholder="example.org" value={domain} onChange={(e) => setDomain(e.target.value)} />
            <Input aria-label="Reason" placeholder="Reason" maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
            <Button type="submit" variant="outline" disabled={add.isPending}>
              Block
            </Button>
          </div>
          {error && (
            <p role="alert" className="text-sm font-medium text-destructive">
              {error}
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  )
}

// ── Imports ──

const IMPORT_NAMES: Record<AdminNonprofitImport['source'], string> = {
  uk_charity_commission: 'UK Charity Commission',
  acnc: 'Australian ACNC',
}

function importBadge(r: AdminNonprofitImport): { kind: StatusKind; label: string } {
  const failedLast = !!r.last_error && (!r.succeeded_at || (r.started_at != null && r.started_at > r.succeeded_at))
  if (failedLast) return { kind: 'down', label: 'Last run failed' }
  if (r.succeeded_at) return { kind: 'up', label: 'OK' }
  if (r.started_at) return { kind: 'pending', label: 'Running' }
  return { kind: 'unknown', label: 'Never run' }
}

function ImportsSection(list: ListProps<AdminNonprofitImport>) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Register imports</CardTitle>
        <CardDescription>Weekly, when the import flag is on. A failed run deletes nothing and retries after 2 hours.</CardDescription>
      </CardHeader>
      <CardContent>
        <ListState {...list} empty="No import sources.">
          {(rows) => (
            <DataTable
              label="Register imports"
              rows={rows}
              getKey={(r) => r.source}
              columns={[
                { header: 'Register', cell: (r) => IMPORT_NAMES[r.source] ?? r.source },
                { header: 'State', cell: (r) => <StatusBadge kind={importBadge(r).kind} label={importBadge(r).label} /> },
                { header: 'Last start', cell: (r) => <span className="num whitespace-nowrap">{day(r.started_at)}</span> },
                { header: 'Last success', cell: (r) => <span className="num whitespace-nowrap">{day(r.succeeded_at)}</span> },
                { header: 'Rows', align: 'right', cell: (r) => <span className="num">{r.rows_seen?.toLocaleString() ?? '—'}</span> },
                { header: 'Domains', align: 'right', cell: (r) => <span className="num">{r.domains?.toLocaleString() ?? '—'}</span> },
                { header: 'Last error', cell: (r) => <span className="break-all text-muted-foreground">{r.last_error ?? '—'}</span> },
              ]}
            />
          )}
        </ListState>
      </CardContent>
    </Card>
  )
}
